import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import MainNavbar from './components/layout/Navbar';
import { AdminDashboard, AdminEventManagement, CreateEvent as AdminCreateEvent, EditEvent as AdminEditEvent, EventList, EventParticipants, UserManagement, AssociationCodeManager, DualRoleDashboard, TeamManagement, TeamDocuments, MonitoringDashboard, SdaDashboard, AdminMembershipDashboard, UispSyncManager } from './components/AdminPages';
import SdaEventPlanning from './components/AdminPages/SdaEventPlanning';
import PaymentManagement from './components/AdminPages/PaymentManagement';
import CreatePaymentRequest from './components/AdminPages/CreatePaymentRequest';
import StripeOnboarding from './components/AdminPages/StripeOnboarding';
import BankAccountConfig from './components/AdminPages/BankAccountConfig';
import { AssociationDashboard, EventDetails, SelectMembers, MemberManagement } from './components/AssociationPages';
import EventPaymentNew from './components/AssociationPages/EventPaymentNew';
import TeamRegistration from './components/AssociationPages/TeamRegistration';
import TeamRegistrationDetails from './components/AssociationPages/TeamRegistrationDetails';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Unauthorized from './components/shared/Unauthorized';
import NotFound from './components/shared/NotFound';
import ErrorPage from './components/shared/ErrorPage';
import ChangePassword from './components/shared/ChangePassword';
import Footer from './components/layout/Footer';

// Import styles
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles/global.css';
import './styles/uisp-theme.css';

// Component to select the right dashboard based on user role
const DashboardSelector = () => {
  const { user, isDualRole } = useAuth();
  
  // If user has dual role (committee + association), show DualRoleDashboard
  if (isDualRole && isDualRole()) {
    return <DualRoleDashboard />;
  }
  
  // Otherwise show regular admin dashboard
  return <AdminDashboard />;
};

// Routes component (previously in routes.js)
const AppRoutes = () => (
  <Routes>
    {/* Public Routes */}
    <Route path="/" element={<LandingPage />} />
    <Route path="/login" element={<Login />} />    {/* Admin Routes - Committee roles with association_id will see DualRoleDashboard */}
    <Route path="/admin" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'uisp_territorial_secretary',
        'uisp_regional_secretary',
        'uisp_nazionale_secretary',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <DashboardSelector />
      </ProtectedRoute>
    } />
    <Route path="/admin/monitoring" element={
      <ProtectedRoute allowedRoles={['admin']}>
        <MonitoringDashboard />
      </ProtectedRoute>
    } />
    <Route path="/admin/membership-dashboard" element={
      <ProtectedRoute allowedRoles={['admin', 'sda_regional', 'sda_territorial']}>
        <AdminMembershipDashboard />
      </ProtectedRoute>
    } />
    <Route path="/admin/sda-dashboard" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale'
      ]}>
        <SdaDashboard />
      </ProtectedRoute>
    } />
    <Route path="/admin/sda-event-planning" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale'
      ]}>
        <SdaEventPlanning />
      </ProtectedRoute>
    } />
    <Route path="/admin/user" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_national',
        'uisp_regional',
        'uisp_territorial',
        'uisp_territorial_secretary',
        'uisp_regional_secretary',
        'uisp_nazionale_secretary'
      ]}>
        <UserManagement />
      </ProtectedRoute>
    } />
    <Route path="/admin/association-codes" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_national',
        'uisp_regional',
        'uisp_territorial'
      ]}>
        <AssociationCodeManager />
      </ProtectedRoute>
    } />
    <Route path="/admin/uisp-sync" element={
      <ProtectedRoute allowedRoles={['admin']}>
        <UispSyncManager />
      </ProtectedRoute>
    } />

    {/* Event Management Routes */}
    <Route path="/admin/events" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <AdminEventManagement />
      </ProtectedRoute>
    } />
    <Route path="/admin/events/create" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <AdminCreateEvent />
      </ProtectedRoute>
    } />
    <Route path="/admin/events/list" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <EventList />
      </ProtectedRoute>
    } />
    <Route path="/admin/events/:id/edit" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale'
      ]}>
        <AdminEditEvent />
      </ProtectedRoute>
    } />
    <Route path="/admin/events/:id/participants" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <EventParticipants />
      </ProtectedRoute>
    } />
    <Route path="/admin/events/:id/register/:adminAssociationId" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <SelectMembers />
      </ProtectedRoute>
    } />

    <Route path="/admin/events/:id/team-registration/:adminAssociationId" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <TeamRegistration />
      </ProtectedRoute>
    } />
    <Route path="/admin/events/:id/teams/manage" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <TeamManagement />
      </ProtectedRoute>
    } />
    <Route path="/admin/events/:id/teams/documents" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ]}>
        <TeamDocuments />
      </ProtectedRoute>
    } />

    {/* Payment Management Routes */}
    <Route path="/admin/payments" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national'
      ]}>
        <PaymentManagement />
      </ProtectedRoute>
    } />
    <Route path="/admin/payments/create" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national'
      ]}>
        <CreatePaymentRequest />
      </ProtectedRoute>
    } />
    <Route path="/admin/bank-accounts" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national'
      ]}>
        <BankAccountConfig />
      </ProtectedRoute>
    } />
    <Route path="/admin/stripe-onboarding" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national'
      ]}>
        <StripeOnboarding />
      </ProtectedRoute>
    } />

    {/* Association Routes */}
    <Route path="/association" element={<ProtectedRoute allowedRoles={['association']}><AssociationDashboard /></ProtectedRoute>} />
    <Route path="/association/events/:id" element={<ProtectedRoute allowedRoles={['association']}><EventDetails /></ProtectedRoute>} />
    <Route path="/association/events/:id/select-members" element={<ProtectedRoute allowedRoles={['association']}><SelectMembers /></ProtectedRoute>} />
    <Route path="/association/events/:id/team-registration" element={<ProtectedRoute allowedRoles={['association']}><TeamRegistration /></ProtectedRoute>} />
    <Route path="/association/events/:id/teams" element={<ProtectedRoute allowedRoles={['association']}><TeamRegistrationDetails /></ProtectedRoute>} />
    <Route path="/association/events/:id/payment" element={<ProtectedRoute allowedRoles={['association']}><EventPaymentNew /></ProtectedRoute>} />
    <Route path="/association/members" element={<ProtectedRoute allowedRoles={['association']}><MemberManagement /></ProtectedRoute>} />

    {/* Error Routes */}
    <Route path="/unauthorized" element={<Unauthorized />} />
    <Route path="/error" element={<ErrorPage />} />
    <Route path="/change-password" element={
      <ProtectedRoute allowedRoles={[
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'uisp_territorial_secretary',
        'uisp_regional_secretary',
        'uisp_nazionale_secretary',
        'sda_territorial',
        'sda_regional',
        'sda_nazionale',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale',
        'association'
      ]}>
        <ChangePassword />
      </ProtectedRoute>
    } />
    
    {/* 404 - Must be last */}
    <Route path="*" element={<NotFound />} />

  </Routes>
);

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AuthProvider>
        <div className="d-flex flex-column min-vh-100">
          <MainNavbar />
          <main className="flex-grow-1">
            <AppRoutes />
          </main>
          <Footer />
        </div>
      </AuthProvider>
    </Router>
  );
}

// Main rendering (previously in index.js)
const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

export default App;

